import { useEffect, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { metadataSchema } from "../../yup";
import Input from "../../components/UserInput/Input";
import { useCreateMetadataMutation } from "../../services/api";
import style from "./metadata.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { getErrorMessage } from "../../utils/common";

type FormState = typeof metadataSchema.__outputType;

/**
 * Add Metadata page for creating a new metadata record.
 *
 * Notes:
 * - Validation is enforced via `metadataSchema`.
 * - The post-save navigation timer is cleared on unmount to avoid navigating
 *   after the component has been removed from the tree.
 */
const AddMetadata = () => {
  const [createMetadata] = useCreateMetadataMutation();
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const navigation = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, []);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(metadataSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormState>({
    resolver: yupResolver(metadataSchema),
    mode: "onChange",
  });

  /**
   * Submits the new metadata payload.
   *
   * Args:
   *   values (FormState): Form values for the new metadata.
   *
   * Returns:
   *   Promise<void>
   *
   * Raises:
   *   Error: Propagated when the create mutation fails.
   */
  const onSubmit = async (values: FormState) => {
    try {
      setIsLoading(true);
      const { name } = values;
      const payload: AddMetadataPayload = {
        name: name,
      };

      await createMetadata(payload).unwrap();
      setIsLoading(false);
      setMessage({
        type: "success",
        text: "Metadata added successfully.",
      });
      navigationTimeoutRef.current = setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Please enter valid details"),
      });
      setIsLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Typography className={style.heading}>Add Metadata</Typography>
      <Typography className={style.description}>
        Enter the below details to start adding a new metadata to Pegasus
        Cybersoft.
      </Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box className={style.flexTop}>
          <Box className={style.row}>
            <Input
              placeholder="e.g Gross Description"
              label="Name"
              required
              register={register}
              name="name"
              errors={errors}
            />
          </Box>
        </Box>
        {message && (
          <Box mt={2}>
            <LabelBox
              boxProps={{ height: 50, display: "flex", alignItems: "center" }}
              data={message}
              onClose={() => setMessage(null)}
            />
          </Box>
        )}
        <Box className={style.buttons}>
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: "transparent" },
            }}
            variant="outlined"
            onClick={() => navigation(-1)}
            label={"Cancel"}
          />
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: colors.primary },
            }}
            variant="contained"
            type="submit"
            label={"Save"}
            loading={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default AddMetadata;
